import type { AuditLogRow, AuditSummary, Lookups, RefCollector, Summarizer, TargetKind } from "./index";
import {
  arr,
  asRecord,
  buildSummary,
  countText,
  entityLabelOf,
  fieldsFragment,
  isUuid,
  missingText,
  num,
  plural,
  resolvePerson,
  resolveTarget,
  str,
  type Resolution,
} from "./vocab";

// ---------------------------------------------------------------------------
// Content and settings domain: benefits, News, home highlights, interests,
// venues, staff roles, communications, club and system settings, and the
// legacy tables (spaces, menu, benefit categories) that no longer exist.
// ---------------------------------------------------------------------------

/** Kind-specific not-found texts the shared vocab leaves as "removido do sistema". */
const CONTENT_MISSING_TEXT = {
  venue: "local removido",
  interest: "interesse removido",
  interest_category: "categoria de interesse removida",
  staff_role: "papel removido",
  communication: "comunicação removida",
  communication_template: "modelo de comunicação removido",
} as const satisfies Partial<Record<TargetKind, string>>;

/** Tables dropped from the schema; their rows can only be described statically. */
const LEGACY = {
  benefitCategory: "Categoria de benefício do modelo antigo",
  space: "Espaço do modelo antigo",
  spaceAmenity: "Comodidade do modelo antigo",
  spaceType: "tipo de Espaço do modelo antigo",
  menuCategory: "Categoria do cardápio do modelo antigo",
  menuItem: "Item do cardápio do modelo antigo",
} as const;

const SYSTEM_SETTING_LABELS: Record<string, string> = {
  checkout_enabled: "Check-out",
  auto_checkout_hours: "Check-out automático",
};

function entityPrimary(row: AuditLogRow): string {
  return entityLabelOf(row, row.action);
}

/** A live target; an unrecorded entity_id falls back to the entity label, never to "removido". */
function liveTarget(row: AuditLogRow, lookups: Lookups, kind: TargetKind): Resolution | string {
  if (!isUuid(row.entity_id)) return entityPrimary(row);
  const res = resolveTarget(lookups, kind, row.entity_id);
  if (res.state !== "missing") return res;
  return { ...res, text: (CONTENT_MISSING_TEXT as Partial<Record<TargetKind, string>>)[kind] ?? res.text };
}

/** A target the row itself proves is gone (delete rows, dropped tables). */
function goneTarget(row: AuditLogRow, text: string): Resolution {
  return isUuid(row.entity_id) ? { text, state: "missing", id: row.entity_id } : { text, state: "missing" };
}

function live(kind: TargetKind, withFields: boolean): Summarizer {
  return (row, lookups) => buildSummary(liveTarget(row, lookups, kind), [withFields && fieldsFragment(row.details)]);
}

function gone(text: string, withFields = false): Summarizer {
  return (row) => buildSummary(goneTarget(row, text), [withFields && fieldsFragment(row.details)]);
}

/** Primary built from `details.count`; the entity label when the count is absent. */
function counted(singular: string, pluralForm: string, zero?: string): Summarizer {
  return (row) => buildSummary(countText(row.details, { zero, one: singular, many: pluralForm }) ?? entityPrimary(row));
}

/** A legacy parent (dropped table) plus a count of what was synced under it. */
function legacySync(text: string, singular: string, pluralForm: string, zero: string): Summarizer {
  return (row) =>
    buildSummary(goneTarget(row, text), [countText(row.details, { zero, one: singular, many: pluralForm })]);
}

function feedImageSynced(row: AuditLogRow, lookups: Lookups): AuditSummary {
  return buildSummary(liveTarget(row, lookups, "feed_post"), [
    countText(row.details, { zero: "sem imagens", one: "imagem", many: "imagens" }),
  ]);
}

function staffRoleCreated(row: AuditLogRow, lookups: Lookups): AuditSummary {
  const snapshot = str(row.details, "name");
  return buildSummary(snapshot ?? liveTarget(row, lookups, "staff_role"));
}

function staffVenueCoverage(row: AuditLogRow, lookups: Lookups): AuditSummary {
  const primary = isUuid(row.entity_id) ? resolvePerson(lookups, row.entity_id) : entityPrimary(row);
  const total = num(row.details, "total");
  const added = num(row.details, "added");
  const removed = num(row.details, "removed");
  return buildSummary(primary, [
    total !== null && (total === 0 ? "não cobre nenhum local" : `cobre ${plural(total, "local", "locais")}`),
    added !== null && added > 0 && plural(added, "adicionado", "adicionados"),
    removed !== null && removed > 0 && plural(removed, "removido", "removidos"),
  ]);
}

function formatSettingValue(key: string | null, value: unknown): string | null {
  if (typeof value === "boolean") {
    if (key === "checkout_enabled") return value ? "ligado" : "desligado";
    return value ? "sim" : "não";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return key === "auto_checkout_hours" ? `após ${value} h` : String(value);
  }
  if (typeof value === "string" && value.trim() !== "") return value;
  return null;
}

function systemSettingUpserted(row: AuditLogRow): AuditSummary {
  const key = str(row.details, "key");
  const primary = key ? SYSTEM_SETTING_LABELS[key] ?? key : entityPrimary(row);
  return buildSummary(primary, [formatSettingValue(key, asRecord(row.details)?.value)]);
}

function tenantSettingsUpdated(row: AuditLogRow): AuditSummary {
  const rotated = arr(row.details, "rotated_secrets")?.filter((s) => typeof s === "string" && s !== "").length ?? 0;
  return buildSummary("Configurações do clube", [
    fieldsFragment(row.details),
    rotated > 0 && `trocou ${plural(rotated, "credencial", "credenciais")}`,
  ]);
}

function staticWithFields(text: string): Summarizer {
  return (row) => buildSummary(text, [fieldsFragment(row.details)]);
}

/** Summaries for the content and settings domain, keyed by audit_log.action. */
export const summarizers: Partial<Record<string, Summarizer>> = {
  benefit_created: live("benefit", false),
  benefit_updated: live("benefit", true),
  benefit_deleted: gone(missingText("benefit")),
  benefit_reordered: counted("benefício", "benefícios"),

  benefit_category_created: gone(LEGACY.benefitCategory),
  benefit_category_updated: gone(LEGACY.benefitCategory, true),
  benefit_category_deleted: gone(LEGACY.benefitCategory),
  benefit_category_reordered: counted("categoria de benefício", "categorias de benefício"),

  feed_post_created: live("feed_post", false),
  feed_post_updated: live("feed_post", true),
  feed_post_deleted: gone(missingText("feed_post")),
  feed_image_synced: feedImageSynced,
  home_hero_set: counted("destaque", "destaques", "nenhum destaque"),

  interest_created: live("interest", false),
  interest_updated: live("interest", true),
  interest_deleted: gone(CONTENT_MISSING_TEXT.interest),
  interest_category_created: live("interest_category", false),
  interest_category_updated: live("interest_category", true),
  interest_category_deleted: gone(CONTENT_MISSING_TEXT.interest_category),
  interest_form_updated: staticWithFields("Ficha de interesse"),

  venue_created: live("venue", false),
  venue_updated: live("venue", true),
  venue_deleted: gone(CONTENT_MISSING_TEXT.venue),

  space_created: gone(LEGACY.space),
  space_updated: gone(LEGACY.space, true),
  space_deleted: gone(LEGACY.space),
  space_amenity_created: gone(LEGACY.spaceAmenity),
  space_amenity_deleted: gone(LEGACY.spaceAmenity),
  space_amenity_links_synced: legacySync(LEGACY.space, "comodidade", "comodidades", "sem comodidades"),
  space_type_created: gone(LEGACY.spaceType),
  space_type_updated: gone(LEGACY.spaceType, true),
  space_type_deleted: gone(LEGACY.spaceType),
  space_type_tier_discount_synced: legacySync(
    LEGACY.spaceType,
    "desconto por plano",
    "descontos por plano",
    "sem desconto por plano",
  ),

  menu_category_created: gone(LEGACY.menuCategory),
  menu_category_updated: gone(LEGACY.menuCategory, true),
  menu_category_deleted: gone(LEGACY.menuCategory),
  menu_item_created: gone(LEGACY.menuItem),
  menu_item_updated: gone(LEGACY.menuItem, true),
  menu_item_deleted: gone(LEGACY.menuItem),

  communication_created: live("communication", false),
  communication_template_created: live("communication_template", false),
  communication_template_deleted: gone(CONTENT_MISSING_TEXT.communication_template),
  notification_bulk_sent: counted("notificação", "notificações"),

  staff_role_created: staffRoleCreated,
  staff_role_updated: live("staff_role", true),
  staff_role_deleted: gone(CONTENT_MISSING_TEXT.staff_role),
  staff_venue_coverage_synced: staffVenueCoverage,

  system_setting_upserted: systemSettingUpserted,
  tenant_settings_updated: tenantSettingsUpdated,
};

function entityTarget(kind: TargetKind): RefCollector {
  return (row, sink) => sink.target(kind, row.entity_id);
}

/** Only rows whose target may still exist ask for a lookup; delete and legacy rows do not. */
export const refCollectors: Partial<Record<string, RefCollector>> = {
  benefit_created: entityTarget("benefit"),
  benefit_updated: entityTarget("benefit"),
  feed_post_created: entityTarget("feed_post"),
  feed_post_updated: entityTarget("feed_post"),
  feed_image_synced: entityTarget("feed_post"),
  interest_created: entityTarget("interest"),
  interest_updated: entityTarget("interest"),
  interest_category_created: entityTarget("interest_category"),
  interest_category_updated: entityTarget("interest_category"),
  venue_created: entityTarget("venue"),
  venue_updated: entityTarget("venue"),
  communication_created: entityTarget("communication"),
  communication_template_created: entityTarget("communication_template"),
  staff_role_created: (row, sink) => {
    if (!str(row.details, "name")) sink.target("staff_role", row.entity_id);
  },
  staff_role_updated: entityTarget("staff_role"),
  staff_venue_coverage_synced: (row, sink) => sink.person(row.entity_id),
};
